/**
 * Shared domain vocabulary. These names mirror the user-facing nouns defined in
 * docs/01-information-architecture.md §3 — if a name changes there, it changes here.
 */

// ── Languages & dialects ──────────────────────────────────────────────────────

export const LANGUAGES = ['es', 'fr', 'pt', 'de', 'it', 'ja', 'ko', 'zh', 'en'] as const;
export type Language = (typeof LANGUAGES)[number];

/**
 * Dialect is a first-class axis, not a voice setting (strategy wedge W3).
 * It propagates into lexicon, grammar, phonology, scoring thresholds, TTS and content.
 */
export const DIALECTS = [
  'es-MX',
  'es-ES',
  'fr-FR',
  'fr-CA',
  'pt-BR',
  'pt-PT',
  'de-DE',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'zh-CN',
  'zh-TW',
  'en-US',
  'en-GB',
] as const;
export type Dialect = (typeof DIALECTS)[number];

export const DIALECTS_BY_LANGUAGE: Readonly<Record<Language, readonly Dialect[]>> = {
  es: ['es-MX', 'es-ES'],
  fr: ['fr-FR', 'fr-CA'],
  pt: ['pt-BR', 'pt-PT'],
  de: ['de-DE'],
  it: ['it-IT'],
  ja: ['ja-JP'],
  ko: ['ko-KR'],
  zh: ['zh-CN', 'zh-TW'],
  en: ['en-US', 'en-GB'],
};

export function languageOf(dialect: Dialect): Language {
  return dialect.slice(0, 2) as Language;
}

/** True when the language has more than one shipped variant and therefore needs onboarding step 2. */
export function hasDialectChoice(language: Language): boolean {
  return (DIALECTS_BY_LANGUAGE[language]?.length ?? 0) > 1;
}

// ── CEFR ──────────────────────────────────────────────────────────────────────

export const CEFR_SUBLEVELS = [
  'A1.1',
  'A1.2',
  'A2.1',
  'A2.2',
  'A2.3',
  'B1.1',
  'B1.2',
  'B1.3',
  'B2.1',
  'B2.2',
  'B2.3',
  'C1.1',
  'C1.2',
] as const;
export type CefrSublevel = (typeof CEFR_SUBLEVELS)[number];
export type CefrBand = 'A1' | 'A2' | 'B1' | 'B2' | 'C1';

export function cefrBand(sub: CefrSublevel): CefrBand {
  return sub.split('.')[0] as CefrBand;
}

export function cefrIndex(sub: CefrSublevel): number {
  return CEFR_SUBLEVELS.indexOf(sub);
}

export function cefrFromIndex(index: number): CefrSublevel {
  const clamped = Math.max(0, Math.min(CEFR_SUBLEVELS.length - 1, Math.round(index)));
  // Safe: clamped is within bounds by construction.
  return CEFR_SUBLEVELS[clamped] as CefrSublevel;
}

// ── Items & skills ────────────────────────────────────────────────────────────

export const ITEM_TYPES = [
  'word',
  'phrase',
  'grammar',
  'phoneme',
  'pattern',
  'collocation',
] as const;
export type ItemType = (typeof ITEM_TYPES)[number];

export const PILLARS = ['speak', 'words', 'grammar', 'listen', 'confidence'] as const;
export type Pillar = (typeof PILLARS)[number];

/**
 * Review modalities. `production` and `minimalPair` require learner speech; an item is
 * never considered mastered until it has passed a production card (docs/05 §4).
 */
export const MODALITIES = [
  'recognition',
  'production',
  'listening',
  'spelling',
  'cloze',
  'minimalPair',
] as const;
export type Modality = (typeof MODALITIES)[number];

export const SPOKEN_MODALITIES: readonly Modality[] = ['production', 'minimalPair'];

export function isSpokenModality(m: Modality): boolean {
  return SPOKEN_MODALITIES.includes(m);
}

// ── Sessions ──────────────────────────────────────────────────────────────────

export const SESSION_KINDS = [
  'lesson',
  'scenario',
  'roleplay',
  'open',
  'drill',
  'review',
  'listen',
  'assessment',
  'placement',
  'interview',
  'debate',
] as const;
export type SessionKind = (typeof SESSION_KINDS)[number];

/**
 * What to call each kind of session to a learner.
 *
 * Here rather than per screen because F1's history list, the live header and the report
 * card all name the same thing, and one web file already had a *partial* map that
 * silently fell through to the raw enum for `lesson`, `drill`, `review`, `listen` and
 * `placement`. A learner does not know what "roleplay" is; they know "Role-play".
 *
 * `Record` rather than `Partial<Record>` on purpose: adding a kind without a label is a
 * type error, which is the only reason this cannot drift again.
 */
export const SESSION_KIND_LABELS: Readonly<Record<SessionKind, string>> = {
  lesson: 'Lesson',
  scenario: 'Conversation',
  roleplay: 'Role-play',
  open: 'Open talk',
  drill: 'Drill',
  review: 'Review',
  listen: 'Listening',
  assessment: 'Level check',
  placement: 'Placement',
  interview: 'Interview practice',
  debate: 'Debate',
};

export type SessionMode = 'voice' | 'text';

// ── Scoring ───────────────────────────────────────────────────────────────────

/**
 * The five reported dimensions, chosen as the union of the best in the market:
 * YayTalk's accuracy/fluency/completeness, Stimuler's intonation, TalkMe's confidence.
 * See docs/research/02-market-map.md §2.
 */
/**
 * The five scored dimensions.
 *
 * `accuracy` and `intonation` are nullable because they are the two that genuinely
 * cannot be derived without a pronunciation-assessment engine: accuracy needs
 * phone-level alignment and intonation needs a pitch contour. The other three come from
 * the transcript and its timings, which a plain ASR gives us.
 *
 * Null means "not measured", and every surface renders it as absent rather than as a
 * zero or a dash that reads like a bad score. This is the same commitment as
 * `ScoreStatus.withheld`, one level down: we do not publish a number we cannot
 * substantiate (docs/00-product-strategy.md W2).
 */
/**
 * Every dimension is nullable: any of them can go unmeasured, and null is how we say so.
 *
 * `overallScore` already renormalises its weights around whichever ones are present, so
 * nothing here needs a zero to stand in for a missing measurement — and a zero that
 * stands in for one is a score the learner never earned.
 */
export interface ScoreDimensions {
  readonly accuracy: number | null;
  readonly fluency: number | null;
  readonly completeness: number | null;
  readonly intonation: number | null;
  readonly confidence: number | null;
}

export const SCORE_DIMENSIONS = [
  'accuracy',
  'fluency',
  'completeness',
  'intonation',
  'confidence',
] as const satisfies readonly (keyof ScoreDimensions)[];

/**
 * `withheld` is a first-class outcome. It exists because we refuse to assert an error we
 * cannot substantiate (strategy wedge W2) — the schema must be able to express
 * "we chose not to judge", which most competitors' models cannot.
 */
export type ScoreStatus = 'scored' | 'reduced' | 'withheld' | 'unavailable' | 'pending';

export const SCORE_BANDS = [
  { min: 90, id: 'native_like', label: 'Native-like' },
  { min: 75, id: 'great', label: 'Great' },
  { min: 60, id: 'good', label: 'Good' },
  { min: 40, id: 'getting_there', label: 'Getting there' },
  { min: 0, id: 'keep_going', label: 'Keep going' },
] as const;

export type ScoreBandId = (typeof SCORE_BANDS)[number]['id'];

export interface ScoreBand {
  readonly id: ScoreBandId;
  readonly label: string;
}

/**
 * Scores are never shown as a bare number — colour alone is not an accessible signal
 * and a number alone is not a human one (docs/03-design-system.md §2).
 */
export function bandOf(score: number): ScoreBand {
  const clamped = clamp(score, 0, 100);
  for (const band of SCORE_BANDS) {
    if (clamped >= band.min) return { id: band.id, label: band.label };
  }
  return { id: 'keep_going', label: 'Keep going' };
}

// ── Small shared helpers ──────────────────────────────────────────────────────

export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}

export function round(value: number, decimals = 0): number {
  const f = 10 ** decimals;
  return Math.round(value * f) / f;
}

export const DAY_MS = 86_400_000;
